// Operation tracing. Every step recorded here is emitted by the engine at the
// moment it performs that step — the UI renders traces, it never invents them.
// A trace is the honest answer to "what did this call actually do?": which
// components were touched, in what order, and at what cost.

/** Which part of the engine a step touched (drives the UI's lane colouring). */
export type TraceComponent =
  | "wal"
  | "memtable"
  | "sstable"
  | "manifest"
  | "merge"
  | "result";

export interface TraceStep {
  /** 1-based position within the trace. */
  n: number;
  /** Machine-readable step kind, e.g. "bloom-reject". */
  kind: string;
  component: TraceComponent;
  /** What happened, in one line. */
  detail: string;
  /** The cost this step paid: bytes written, entries scanned, comparisons. */
  cost?: string;
  /** Nesting: per-level steps sit at 1, per-table steps at 2. */
  depth: number;
}

export interface Trace {
  id: number;
  /** The engine call being traced. */
  op: string;
  /** Its argument, for display (usually the key). */
  arg: string;
  summary: string;
  steps: TraceStep[];
  ms: number;
}

export class TraceBuilder {
  private steps: TraceStep[] = [];
  private readonly t0 = performance.now();

  constructor(
    private readonly id: number,
    private readonly op: string,
    private readonly arg: string,
  ) {}

  step(
    kind: string,
    component: TraceComponent,
    detail: string,
    cost?: string,
    depth = 0,
  ): void {
    this.steps.push({
      n: this.steps.length + 1,
      kind,
      component,
      detail,
      cost,
      depth,
    });
  }

  end(summary: string): Trace {
    return {
      id: this.id,
      op: this.op,
      arg: this.arg,
      summary,
      steps: this.steps,
      ms: performance.now() - this.t0,
    };
  }
}
